import { defineStore } from 'pinia';
import { ref } from 'vue';
import { captureEvent } from '../analytics';

/**
 * A client returned by the backend `/api/client` endpoints.
 *
 * The contract uses lowercase keys (`id`, `name`); `id` is the client's
 * UUIDv7 `record_id` serialized as a string.
 */
export interface Client {
    id: string;
    name: string;
}

export const useClientStore = defineStore('client', () => {
    const searching = ref(false);
    const creating = ref(false);
    const error = ref<string | null>(null);

    /**
     * Search existing clients by name. Returns the matches (empty array when
     * nothing matches or the query is blank). Results are capped server-side
     * (CLIENT_RESULT_LIMIT). Backend: GET /api/client?search=
     */
    async function searchClients(query: string): Promise<Client[]> {
        try {
            searching.value = true;
            error.value = null;

            const response = await fetch(
                `/api/client?search=${encodeURIComponent(query)}`,
            );
            if (!response.ok) {
                throw new Error('Failed to search clients');
            }
            return (await response.json()) as Client[];
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            searching.value = false;
        }
    }

    /**
     * Create a new client from a name and return the persisted record.
     * Backend: POST /api/client  body { name }  ->  { id, name } (201).
     * A duplicate name (case-insensitive) yields 409.
     */
    async function createClient(name: string): Promise<Client> {
        try {
            creating.value = true;
            error.value = null;

            const response = await fetch('/api/client', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            });
            if (response.status === 409) {
                throw new Error(`A client named "${name}" already exists.`);
            }
            if (!response.ok) {
                throw new Error('Failed to create client');
            }
            const created = (await response.json()) as Client;
            // id only — never the client name (see analytics PII policy).
            captureEvent('client_created', { client_id: created.id });
            return created;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            creating.value = false;
        }
    }

    return {
        searching,
        creating,
        error,
        searchClients,
        createClient,
    };
});
